import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Mail, Phone, Pencil, TrendingUp, UserPlus, Wallet, RefreshCw } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import {
    listTenants, listUnits, listProperties, createTenant, updateTenant, deleteTenant,
    aktuelleMieten, mietanpassungNachziehen,
} from "@/lib/api";
import { euro, germanDate, UNIT_TYPES } from "@/lib/format";
import { buildLageMap, propertyDisplay } from "@/lib/propertyLabels";
import { useSchreibschutz } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import DeleteConfirm from "@/components/DeleteConfirm";
import MietanpassungDialog from "@/components/MietanpassungDialog";
import MieterKontoDialog from "@/components/MieterKontoDialog";
import { useTableSort, SortHeader, sortOptions } from "@/components/SortHeader";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { toast } from "sonner";
import { Hilfe } from "@/hilfe/Hilfe";
import { Seitenkopf } from "@/hilfe/Seitenkopf";
import { sperre } from "@/hilfe/Knopfhilfe";

const empty = {
    full_name: "", personen: [], email: "", phone: "", unit_id: "", status: "aktiv",
    mietart: "kalt_nk",
    kaltmiete: "", nebenkosten: "", heizkosten: "", pauschalmiete: "",
    kaution: "",
    einzugsdatum: "", auszugsdatum: "", uebergabe_datum: "", new_address: "", notes: "",
};

// Personen im Vertrag (Auftrag 7, R7). Ein gemeinsamer Mietvertrag bleibt EIN
// Mietverhaeltnis; die Personen sind Zeilen darin. Das Namensfeld leitet der
// Server daraus ab (aktive Vertragspartner, " / ") -- der Dialog zerlegt
// NICHTS selbst, damit es nur eine Zerlegungsregel gibt (personen.py).
const ROLLEN = [
    { wert: "vertragspartner", text: "Vertragspartner" },
    { wert: "bewohner", text: "Bewohner (haftet nicht)" },
];
const neuePerson = (felder = {}) => ({
    name: "", rolle: "vertragspartner", email: "", telefon: "", von: "", bis: "", notiz: "",
    _neu: true, ...felder,
});
// Was der Server aus der Liste als Namen ableiten wird -- nur zur Anzeige.
const nameAusPersonen = (personen) => personen
    .filter((p) => (p.rolle || "vertragspartner") === "vertragspartner" && !p.bis && p.name?.trim())
    .map((p) => p.name.trim())
    .join(" / ");
// Leere Eingaben als null schicken, Hilfsfelder des Dialogs weglassen.
const personenFuerServer = (personen) => personen.map(({ _neu, ...p }) => ({
    name: (p.name || "").trim(),
    rolle: p.rolle || "vertragspartner",
    email: p.email || null, telefon: p.telefon || null,
    von: p.von || null, bis: p.bis || null, notiz: p.notiz || null,
}));

// Die Personen eines Vertrags als Zeilen. Gespeicherte Personen werden nicht
// geloescht, sie scheiden ueber "ausgeschieden am" aus -- das ist
// Vertragsgeschichte. Entfernen gibt es nur fuer Zeilen, die noch nicht
// gespeichert sind.
const PersonenZeilen = ({ form, setForm }) => {
    // Der Name im Vertrag folgt der Liste, solange niemand etwas anderes
    // eingetragen hat ("_name_frei"). Danach gilt der eingetragene Name --
    // "Fam. Passenheim" darf neben "Anna / Jan Passenheim" stehen (Nachtrag
    // zu R7). Massgeblich ist der Server; hier nur dieselbe Regel zur Anzeige.
    const mitListe = (personen) => ({
        ...form, personen,
        full_name: form._name_frei ? form.full_name : nameAusPersonen(personen),
    });
    const setze = (i, feld, wert) => setForm(
        mitListe(form.personen.map((p, j) => (j === i ? { ...p, [feld]: wert } : p))));
    const abgeleitet = nameAusPersonen(form.personen);
    const eingetragen = (form.full_name || "").trim();
    const weichtAb = eingetragen && abgeleitet && eingetragen !== abgeleitet;
    return (
        <div className="col-span-2 space-y-2" data-testid="personen-zeilen">
            <div>
                <Label>Name im Vertrag *<Hilfe id="name-im-vertrag" /></Label>
                <Input value={form.full_name ?? ""} data-testid="input-tenant-name"
                       onChange={(e) => setForm({ ...form, full_name: e.target.value,
                                                  _name_frei: e.target.value.trim() !== "" })} />
                {weichtAb ? (
                    <div className="mt-1 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 p-2"
                         data-testid="hinweis-name-weicht-ab">
                        Weicht von den Personen ab — aus der Liste ergäbe sich „{abgeleitet}“.
                        Gilt so für den Vertrag; die Personen bleiben, wie sie sind.{" "}
                        <button type="button" className="underline"
                                onClick={() => setForm({ ...form, full_name: abgeleitet, _name_frei: false })}>
                            Aus den Personen übernehmen
                        </button>
                    </div>
                ) : !eingetragen ? (
                    <p className="mt-1 text-xs text-gray-500">
                        Leer: wird beim Speichern aus den Personen gebildet{abgeleitet ? ` („${abgeleitet}“)` : ""}.
                    </p>
                ) : null}
            </div>
            <Label className="text-sm font-semibold text-slate-900">Personen im Vertrag<Hilfe id="personen-im-vertrag" /></Label>
            {form.personen.map((p, i) => (
                <div key={i} className={`grid grid-cols-6 gap-2 p-2 rounded-md border ${p.bis ? "border-gray-200 bg-gray-50 opacity-75" : "border-gray-200"}`}
                     data-testid={`person-${i}`}>
                    <div className="col-span-3">
                        <Label className="text-xs">Name<Hilfe id="person-name" /></Label>
                        <Input value={p.name} onChange={(e) => setze(i, "name", e.target.value)} data-testid={`person-${i}-name`} />
                    </div>
                    <div className="col-span-3">
                        <Label className="text-xs">Rolle<Hilfe id="person-rolle" /></Label>
                        <SuchAuswahl wert={p.rolle} onWaehlen={(v) => setze(i, "rolle", v)}
                                     optionen={ROLLEN} testId={`person-${i}-rolle`} />
                    </div>
                    <div className="col-span-3">
                        <Label className="text-xs">E-Mail</Label>
                        <Input value={p.email} onChange={(e) => setze(i, "email", e.target.value)} />
                    </div>
                    <div className="col-span-3">
                        <Label className="text-xs">Telefon</Label>
                        <Input value={p.telefon} onChange={(e) => setze(i, "telefon", e.target.value)} />
                    </div>
                    <div className="col-span-3">
                        <Label className="text-xs">im Vertrag seit<Hilfe id="person-von" /></Label>
                        <Input type="date" value={p.von} onChange={(e) => setze(i, "von", e.target.value)} />
                    </div>
                    <div className="col-span-3">
                        <Label className="text-xs">ausgeschieden am<Hilfe id="person-bis" /></Label>
                        <Input type="date" value={p.bis} onChange={(e) => setze(i, "bis", e.target.value)}
                               data-testid={`person-${i}-bis`} />
                    </div>
                    {p.notiz && (
                        <div className="col-span-6 text-xs text-amber-800">{p.notiz}</div>
                    )}
                    {p._neu && (
                        <div className="col-span-6 text-right">
                            <Button type="button" variant="ghost" size="sm"
                                    onClick={() => setForm(mitListe(form.personen.filter((_x, j) => j !== i)))}>
                                Zeile entfernen
                            </Button>
                        </div>
                    )}
                </div>
            ))}
            <Button type="button" variant="outline" size="sm"
                    onClick={() => setForm(mitListe([...form.personen, neuePerson()]))}
                    data-testid="btn-person-hinzufuegen">
                <UserPlus size={14} className="mr-1" /> Person hinzufügen
            </Button>
            <p className="text-xs text-gray-500">
                Zieht eine Person aus, der Vertrag aber läuft weiter: „ausgeschieden am“ setzen —
                kein Mieterwechsel. Endet der Vertrag, das Mietende unten eintragen.
            </p>
        </div>
    );
};

const toForm = (t) => ({
    ...empty,
    ...t,
    personen: (t.personen || []).map((p) => ({ ...neuePerson(), ...p, _neu: false,
        email: p.email ?? "", telefon: p.telefon ?? "", von: p.von ?? "", bis: p.bis ?? "",
        notiz: p.notiz ?? "" })),
    // Schon gespeichert mit eigenem Vertragsnamen? Dann folgt der Name der
    // Liste nicht mehr von selbst.
    _name_frei: Boolean(t.name_abweichung),
    kaltmiete: t.kaltmiete ?? "",
    nebenkosten: t.nebenkosten ?? "",
    heizkosten: t.heizkosten ?? "",
    pauschalmiete: t.pauschalmiete ?? "",
    kaution: t.kaution ?? "",
    unit_id: t.unit_id ?? "",
});

const Tenants = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tenants, setTenants] = useState([]);
    const [units, setUnits] = useState([]);
    const [properties, setProperties] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const { gesperrt, grund } = useSchreibschutz();

    // Filter
    const [cityFilter, setCityFilter] = useState("");
    const [propertyFilter, setPropertyFilter] = useState("");
    const [unitTypFilter, setUnitTypFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [mietartFilter, setMietartFilter] = useState("");
    // Welcher Mieter gerade angepasst wird. Ein Dialog fuer alle Zeilen statt
    // einer je Zeile: sonst haengen 63 Dialoge im Baum.
    const [anpassungFuer, setAnpassungFuer] = useState(null);
    const [kontoFuer, setKontoFuer] = useState(null);
    const [nachziehBusy, setNachziehBusy] = useState(false);

    // Erst zeigen, dann schreiben: Der Trockenlauf nennt die Zahl, und nur
    // wenn es etwas zu tun gibt, wird geschrieben. So steht im Hinweis, WAS
    // passiert ist, statt eines stillen "erledigt".
    const nachziehen = async () => {
        setNachziehBusy(true);
        try {
            const probe = await mietanpassungNachziehen({ apply: false });
            if (!probe.nachgezogen) {
                toast.success("Alles aktuell — keine Anpassung war nachzuziehen",
                              { description: `${probe.geprueft} Mietverhältnisse geprüft` });
                return;
            }
            const r = await mietanpassungNachziehen({ apply: true });
            toast.success(`${r.nachgezogen} Mietverhältnis${r.nachgezogen === 1 ? "" : "se"} aktualisiert`, {
                description: r.mieter.map((m) => `${m.mieter}: ${euro(m.alt)} → ${euro(m.neu)}`)
                    .slice(0, 5).join(" · "),
            });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.error || "Nachziehen fehlgeschlagen");
        } finally { setNachziehBusy(false); }
    };
    const [mieten, setMieten] = useState({ mieten: {}, geplant: {} });

    // Die geltende Miete steht nicht am Mietverhältnis, sondern im
    // Mietverlauf. Ohne diesen Aufruf zeigt die Liste den Vertragsbetrag,
    // während das Soll längst mit der Anpassung rechnet -- genau der
    // Widerspruch, der am 10.09.2026 gemeldet wurde.
    const load = async () => {
        const [t, u, p, m] = await Promise.all([
            listTenants(), listUnits(), listProperties(),
            aktuelleMieten().catch(() => ({ mieten: {}, geplant: {} })),
        ]);
        setTenants(t); setUnits(u); setProperties(p); setMieten(m);
    };
    useEffect(() => { load(); }, []);

    const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
    const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
    const lageMap = useMemo(() => buildLageMap(properties, units), [properties, units]);
    const propertyForTenant = (t) => {
        const u = unitMap[t.unit_id];
        return u ? propMap[u.property_id] : null;
    };
    const sortedUnits = useMemo(() => sortOptions(units, (u) => u.unit_label || ""), [units]);

    // Dropdown-Optionen
    const sortedCities = useMemo(() => {
        const cs = new Set(properties.map((p) => (p.city || "").trim()).filter(Boolean));
        return Array.from(cs).sort((a, b) => a.localeCompare(b, "de"));
    }, [properties]);
    const sortedProperties = useMemo(
        () => sortOptions(properties, (p) => propertyDisplay(p, lageMap)),
        [properties, lageMap]
    );
    const sortedUnitTypes = useMemo(() => sortOptions(UNIT_TYPES), []);

    // Filter anwenden (vor der Volltextsuche)
    const filteredTenants = useMemo(() => {
        return tenants.filter((t) => {
            const u = unitMap[t.unit_id];
            const prop = u ? propMap[u.property_id] : null;
            if (cityFilter && (prop?.city || "").trim() !== cityFilter) return false;
            if (propertyFilter && prop?.id !== propertyFilter) return false;
            if (unitTypFilter && u?.typ !== unitTypFilter) return false;
            if (statusFilter && t.status !== statusFilter) return false;
            if (mietartFilter && t.mietart !== mietartFilter) return false;
            return true;
        });
    }, [tenants, unitMap, propMap, cityFilter, propertyFilter, unitTypFilter, statusFilter, mietartFilter]);

    // Volltextsuche
    const searchFields = useMemo(() => [
        "full_name", "email", "phone", "status", "mietart", "notes",
        (t) => unitMap[t.unit_id]?.unit_label || "",
        (t) => propertyForTenant(t)?.label || "",
        (t) => propertyForTenant(t)?.address || "",
        (t) => propertyForTenant(t)?.plz || "",
        (t) => propertyForTenant(t)?.city || "",
        (t) => { const p = propertyForTenant(t); return p ? (lageMap[p.id] || "") : ""; },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [unitMap, propMap, lageMap]);
    const { query, setQuery, filtered: searched, total, matches } = useFulltextFilter(filteredTenants, searchFields);

    const sortResolvers = useMemo(() => ({
        full_name: (t) => (t.full_name || "").toLowerCase(),
        email: (t) => (t.email || t.phone || "").toLowerCase(),
        property: (t) => (propertyForTenant(t)?.label || propertyForTenant(t)?.address || "").toLowerCase(),
        unit: (t) => (unitMap[t.unit_id]?.unit_label || "").toLowerCase(),
        status: (t) => t.status || "",
        einzugsdatum: (t) => t.einzugsdatum || "",
        miete: (t) => mieten.mieten?.[t.id]?.soll_total
            ?? (t.mietart === "pauschal" ? (t.pauschalmiete ?? null)
                : ((t.kaltmiete || 0) + (t.nebenkosten || 0) + (t.heizkosten || 0)) || null),
        kaution: (t) => t.kaution ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [unitMap, propMap, mieten]);
    const { sortKey, sortDir, toggle, sorted } = useTableSort(searched, "full_name", "asc", sortResolvers);

    const startCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
    const startEdit = (t) => { setEditingId(t.id); setForm(toForm(t)); setOpen(true); };

    // `/mieter?edit=<id>` oeffnet den Mieter direkt im Bearbeiten-Dialog. So
    // fuehrt ein Mietername aus der Objekt- oder Einheitenliste dorthin, wo
    // seine Daten stehen -- eine eigene Mieter-Detailseite gibt es nicht, der
    // Dialog IST die Detailansicht. Der Parameter wird nach dem Oeffnen aus
    // der Adresse entfernt, sonst springt der Dialog bei jedem Zurueck wieder auf.
    useEffect(() => {
        const wunsch = searchParams.get("edit");
        if (!wunsch || !tenants.length) return;
        const t = tenants.find((x) => x.id === wunsch);
        if (t) startEdit(t);
        else toast.error("Mietverhältnis nicht gefunden");
        const rest = new URLSearchParams(searchParams);
        rest.delete("edit");
        setSearchParams(rest, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenants, searchParams]);

    const save = async () => {
        const mitListe = form.personen.length > 0;
        if (mitListe ? !form.personen.some((p) => p.name?.trim())
                     : !form.full_name.trim()) {
            toast.error("Name ist erforderlich"); return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                // Mit Liste gewinnt die Liste, und der Server leitet den Namen
                // ab. Ohne Liste wird sie gar nicht geschickt (undefined faellt
                // beim Serialisieren weg) -- der Datensatz bleibt im Rueckfall.
                personen: mitListe ? personenFuerServer(form.personen) : undefined,
                _name_frei: undefined,
                // Leer ist kein Vertragsname: der Server bildet ihn dann aus
                // der Liste (Nachtrag zu R7, N4).
                full_name: (form.full_name || "").trim(),
                unit_id: form.unit_id || null,
                kaltmiete: form.kaltmiete !== "" ? parseFloat(form.kaltmiete) : null,
                nebenkosten: form.nebenkosten !== "" ? parseFloat(form.nebenkosten) : null,
                heizkosten: form.heizkosten !== "" ? parseFloat(form.heizkosten) : null,
                pauschalmiete: form.pauschalmiete !== "" ? parseFloat(form.pauschalmiete) : null,
                kaution: form.kaution !== "" ? parseFloat(form.kaution) : null,
            };
            if (editingId) {
                await updateTenant(editingId, payload);
                toast.success("Mieter aktualisiert");
            } else {
                await createTenant(payload);
                toast.success("Mieter angelegt");
            }
            setOpen(false); setForm(empty); setEditingId(null); load();
        } catch (e) {
            // Ohne dieses catch verschwand ein 400 still: Der Dialog blieb
            // offen, nichts war gespeichert, und niemand erfuhr warum.
            // Dieselbe Luecke gab es in Units.jsx und ist dort behoben.
            // Dringlich seit dem 13.10.2026: Ein Mietverhaeltnis an einer
            // zimmerweise vermieteten Wohnung weist das Backend jetzt ab --
            // mit einem Satz, der den richtigen Weg nennt (erst umschalten,
            // dann Zimmer anlegen, dann Mieter). Der Satz muss ankommen.
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.error || "Speichern fehlgeschlagen",
                        { description: typeof d === "object" ? d?.detail : null });
        } finally { setSaving(false); }
    };

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Personen</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="tenants-title">Mieter</h1>
                    <p className="text-sm text-gray-500 mt-1">Aktive, gekündigte und ausgezogene Mieter — inkl. Mietkonditionen.</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Schritt 4a: regelmaessige Zahler ohne Mietverhaeltnis -- die
                        Luecke im Nenner der steuernden Zahl. */}
                    {/* Der Lauf kommt taeglich aus dem Zeitplan; der Knopf ist fuer
                        den Fall, dass eine Anpassung gerade erst eingetragen wurde
                        und man nicht bis zur Nacht warten will. */}
                    <Button variant="outline" size="sm" disabled={gesperrt || nachziehBusy}
                            grund={sperre([gesperrt, grund], [nachziehBusy, "Wird ausgeführt"])}
                            hilfe="aktion.mietanpassungNachziehen"
                            onClick={nachziehen} data-testid="btn-anpassungen-nachziehen">
                        <RefreshCw size={14} className={`mr-1.5 ${nachziehBusy ? "animate-spin" : ""}`} />
                        Mieten nachziehen
                    </Button>
                    <Link to="/mieter/fehlend" data-testid="btn-fehlende-mieter">
                        <Button size="sm" variant="outline" hilfe="aktion.fehlendeMietverhaeltnisse">
                            <UserPlus size={14} className="mr-1.5" /> Fehlende Mietverhältnisse
                        </Button>
                    </Link>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={startCreate} data-testid="btn-new-tenant"
                            disabled={gesperrt} grund={grund}>
                        <Plus size={14} className="mr-1.5" /> Neuer Mieter
                    </Button>
                </div>
            </div>
            <Seitenkopf id="seite.mieter" />

            {/* Filterleiste */}
            <div className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Stadt</Label>
                    <SuchAuswahl wert={cityFilter || "all"} onWaehlen={(v) => setCityFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Städte" },
                            ...sortedCities.map((c) => ({ wert: c, text: c })),
                        ]}
                        className="w-[160px]"
                        testId="filter-tenant-city"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Immobilie</Label>
                    <SuchAuswahl wert={propertyFilter || "all"} onWaehlen={(v) => setPropertyFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Immobilien" },
                            ...sortedProperties.map((p) => ({ wert: p.id, text: propertyDisplay(p, lageMap) })),
                        ]}
                        className="w-[240px]"
                        testId="filter-tenant-property"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Art</Label>
                    <SuchAuswahl wert={unitTypFilter || "all"} onWaehlen={(v) => setUnitTypFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Arten" },
                            ...sortedUnitTypes.map((t) => ({ wert: t.value, text: t.label })),
                        ]}
                        className="w-[160px]"
                        testId="filter-tenant-typ"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Status</Label>
                    <SuchAuswahl wert={statusFilter || "all"} onWaehlen={(v) => setStatusFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Status" },
                            { wert: "aktiv", text: "Aktiv" },
                            { wert: "gekuendigt", text: "Gekündigt" },
                            { wert: "ausgezogen", text: "Ausgezogen" },
                        ]}
                        className="w-[150px]"
                        testId="filter-tenant-status"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Miet-Art</Label>
                    <SuchAuswahl wert={mietartFilter || "all"} onWaehlen={(v) => setMietartFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Miet-Arten" },
                            { wert: "kalt_nk", text: "Kalt + Nebenkosten" },
                            { wert: "pauschal", text: "Pauschalmiete" },
                        ]}
                        className="w-[180px]"
                        testId="filter-tenant-mietart"
                    />
                </div>
                <div className="ml-auto flex items-center gap-3">
                    <SearchBox value={query} onChange={setQuery} placeholder="Suche Name, E-Mail, Einheit, Adresse…" testid="tenants-search" className="w-[280px]" />
                    <MatchCount matches={matches} total={total} label="Mieter" />
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editingId ? "Mieter bearbeiten" : "Neuer Mieter"}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3 py-2">
                        {form.personen.length === 0 ? (
                            <div className="col-span-2">
                                <Label>Name *<Hilfe id="namensfeld" /></Label>
                                <Input value={form.full_name ?? ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="input-tenant-name" />
                                {/* Kein Zerlegen im Dialog: EINE Zeile mit dem ganzen
                                    Namen, aufgeteilt wird von Hand (Auftrag 7, F10). */}
                                <Button type="button" variant="outline" size="sm" className="mt-2"
                                        disabled={!form.full_name?.trim()}
                                        grund={sperre([!form.full_name?.trim(), "Kein Name eingetragen"])}
                                        onClick={() => setForm({ ...form, personen: [neuePerson({
                                            name: form.full_name.trim(), email: form.email || "",
                                            telefon: form.phone || "" })] })}
                                        data-testid="btn-personen-erfassen">
                                    <UserPlus size={14} className="mr-1" /> Als Personen erfassen
                                </Button>
                                <p className="mt-1 text-xs text-gray-500">
                                    Mehrere Mieter in einem Vertrag? Erst erfassen, dann je Person eine Zeile.
                                </p>
                            </div>
                        ) : (
                            <PersonenZeilen form={form} setForm={setForm} />
                        )}
                        <div>
                            <Label>E-Mail</Label>
                            <Input value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-tenant-email" />
                        </div>
                        <div>
                            <Label>Telefon</Label>
                            <Input value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-tenant-phone" />
                        </div>
                        <div>
                            <Label>Einheit<Hilfe id="mieter-einheit" /></Label>
                            <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="input-tenant-unit"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— keine —</SelectItem>
                                    {sortedUnits.map((u) => {
                                        const p = propMap[u.property_id];
                                        const pName = p ? (p.label || p.address) : "";
                                        return (
                                            <SelectItem key={u.id} value={u.id}>
                                                {(u.unit_label || u.id.slice(0, 6))}{pName ? ` · ${pName}` : ""}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Status<Hilfe id="mieter-status" /></Label>
                            <SuchAuswahl wert={form.status} onWaehlen={(v) => setForm({ ...form, status: v })}
                                optionen={[
                                    { wert: "aktiv", text: "Aktiv" },
                                    { wert: "gekuendigt", text: "Gekündigt" },
                                    { wert: "ausgezogen", text: "Ausgezogen" },
                                ]}
                                testId="input-tenant-status"
                            />
                        </div>
                        <div>
                            <Label>Einzugsdatum<Hilfe id="einzugsdatum" /></Label>
                            <Input type="date" value={form.einzugsdatum || ""} onChange={(e) => setForm({ ...form, einzugsdatum: e.target.value })} />
                        </div>
                        {/* Zwei Daten, nicht eines: das Mietende beendet das Soll
                            (letzter Miettag, meist ein Monatsletzter), die Übergabe
                            ist die Schlüsselrückgabe und fällt selten auf denselben
                            Tag. Festlegung vom 05.10.2026. */}
                        <div>
                            <Label>Mietende (letzter Miettag)<Hilfe id="mietende" /></Label>
                            <Input type="date" value={form.auszugsdatum || ""}
                                   onChange={(e) => setForm({ ...form, auszugsdatum: e.target.value })}
                                   data-testid="input-tenant-mietende" />
                        </div>
                        <div>
                            <Label>Übergabe (Schlüsselrückgabe)<Hilfe id="uebergabe" /></Label>
                            <Input type="date" value={form.uebergabe_datum || ""}
                                   onChange={(e) => setForm({ ...form, uebergabe_datum: e.target.value })}
                                   data-testid="input-tenant-uebergabe" />
                        </div>

                        {/* Mietart */}
                        <div className="col-span-2">
                            <Label className="text-sm font-semibold text-slate-900">Mietart<Hilfe id="mietart" /></Label>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                                <label className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${form.mietart === "kalt_nk" ? "border-slate-900 bg-slate-50" : "border-gray-200"}`}>
                                    <input type="radio" name="mietart" checked={form.mietart === "kalt_nk"} onChange={() => setForm({ ...form, mietart: "kalt_nk" })} className="mt-1" data-testid="mietart-kalt" />
                                    <div>
                                        <div className="text-sm font-medium">Kaltmiete + Nebenkosten</div>
                                        <div className="text-xs text-gray-600">Übliche Aufteilung mit jährlicher NK-Abrechnung</div>
                                    </div>
                                </label>
                                <label className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${form.mietart === "pauschal" ? "border-slate-900 bg-slate-50" : "border-gray-200"}`}>
                                    <input type="radio" name="mietart" checked={form.mietart === "pauschal"} onChange={() => setForm({ ...form, mietart: "pauschal" })} className="mt-1" data-testid="mietart-pauschal" />
                                    <div>
                                        <div className="text-sm font-medium">Pauschalmiete</div>
                                        <div className="text-xs text-gray-600">Inklusive aller Nebenkosten — keine Abrechnung</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {form.mietart === "kalt_nk" ? (
                            <>
                                <div>
                                    <Label>Kaltmiete (€)<Hilfe id="mietbetrag" /></Label>
                                    <Input type="number" step="0.01" value={form.kaltmiete ?? ""} onChange={(e) => setForm({ ...form, kaltmiete: e.target.value })} data-testid="input-tenant-kalt" />
                                </div>
                                <div>
                                    <Label>Nebenkosten (€)<Hilfe id="mietbetrag" /></Label>
                                    <Input type="number" step="0.01" value={form.nebenkosten ?? ""} onChange={(e) => setForm({ ...form, nebenkosten: e.target.value })} data-testid="input-tenant-nk" />
                                </div>
                                <div>
                                    <Label>Heizkosten (€)<Hilfe id="mietbetrag" /></Label>
                                    <Input type="number" step="0.01" value={form.heizkosten ?? ""} onChange={(e) => setForm({ ...form, heizkosten: e.target.value })} />
                                </div>
                                <div></div>
                            </>
                        ) : (
                            <div className="col-span-2">
                                <Label>Pauschalmiete (€)<Hilfe id="mietbetrag" /></Label>
                                <Input type="number" step="0.01" value={form.pauschalmiete ?? ""} onChange={(e) => setForm({ ...form, pauschalmiete: e.target.value })} placeholder="Gesamtbetrag inkl. NK" data-testid="input-tenant-pauschal" />
                            </div>
                        )}

                        <div className="col-span-2">
                            <Label>Kaution (€)</Label>
                            <Input type="number" step="0.01" value={form.kaution ?? ""} onChange={(e) => setForm({ ...form, kaution: e.target.value })} data-testid="input-tenant-kaution" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                        <Button onClick={save} disabled={saving || gesperrt} grund={sperre([saving, "Wird gespeichert"], [gesperrt, grund])} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-tenant">
                            {saving ? "Speichere…" : (editingId ? "Aktualisieren" : "Speichern")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                            <SortHeader label="Name" sortKey="full_name" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Kontakt" sortKey="email" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Immobilie" sortKey="property" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Einheit" sortKey="unit" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Status" sortKey="status" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Einzug" sortKey="einzugsdatum" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Miete / Mo." sortKey="miete" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Kaution" sortKey="kaution" state={{sortKey, sortDir}} onToggle={toggle} />
                            {/* Die Aktionsspalte bleibt am rechten Rand stehen.
                                Sonst schiebt der waagerechte Bildlauf sie aus dem Bild --
                                und wer "Miete" sucht, findet nur den halben Stift.
                                Gemeldet am 10.09.2026 genau so. */}
                            <th className="text-right font-medium px-4 py-3 sticky right-0 bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map((t) => {
                            const u = unitMap[t.unit_id];
                            const prop = u ? propMap[u.property_id] : null;
                            const vertragsmiete = t.mietart === "pauschal"
                                ? t.pauschalmiete
                                : (t.kaltmiete || 0) + (t.nebenkosten || 0) + (t.heizkosten || 0);
                            const angepasst = mieten.mieten?.[t.id];
                            const geplant = mieten.geplant?.[t.id];
                            const monatsmiete = angepasst?.soll_total ?? vertragsmiete;
                            return (
                                <tr key={t.id} className="group hover:bg-gray-50" data-testid={`tenant-row-${t.id}`}>
                                    <td className="px-4 py-3 font-medium text-slate-900">{t.full_name}</td>
                                    <td className="px-4 py-3 text-gray-500 space-y-0.5">
                                        {t.email && <div className="flex items-center gap-1 text-xs"><Mail size={11} />{t.email}</div>}
                                        {t.phone && <div className="flex items-center gap-1 text-xs"><Phone size={11} />{t.phone}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">
                                        {prop ? (
                                            <>
                                                <div>{propertyDisplay(prop, lageMap)}</div>
                                                {prop.label && prop.address && prop.label !== prop.address && (
                                                    <div className="text-[10px] text-gray-400">{prop.address}</div>
                                                )}
                                            </>
                                        ) : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">{u?.unit_label || "—"}</td>
                                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                                    <td className="px-4 py-3 text-gray-500 tabular-nums">{germanDate(t.einzugsdatum)}</td>
                                    <td className="px-4 py-3 text-gray-700 tabular-nums">
                                        {euro(monatsmiete)}
                                        {/* Kommt der Betrag aus einer Anpassung, muss das
                                            dranstehen -- sonst sucht man den Unterschied im
                                            Mieterdatensatz und findet ihn nicht. */}
                                        {angepasst ? (
                                            <div className="text-[10px] text-emerald-700"
                                                 title={`Angepasst ab ${angepasst.gilt_ab}. Im Mietverhältnis stehen ${euro(vertragsmiete)}.`}
                                                 data-testid={`miete-angepasst-${t.id}`}>
                                                angepasst ab {angepasst.gilt_ab}
                                            </div>
                                        ) : (
                                            <div className="text-[10px] text-gray-400">{t.mietart === "pauschal" ? "Pauschal" : "kalt + NK"}</div>
                                        )}
                                        {geplant && (
                                            <div className="text-[10px] text-amber-700">
                                                geplant ab {geplant.gilt_ab}: {euro(geplant.soll_total)}<Hilfe id="geplant" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 tabular-nums">{euro(t.kaution)}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                                        {/* Lesen, nicht schreiben -- deshalb auch bei
                                            Schreibschutz erreichbar. */}
                                        <Button variant="ghost" size="sm" className="h-8"
                                                onClick={() => setKontoFuer(t)}
                                                data-testid={`zahlungen-tenant-${t.id}`}
                                                hilfe="aktion.mieterKonto">
                                            <Wallet size={12} className="mr-1" /> Zahlungen
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-8"
                                                onClick={() => setAnpassungFuer(t)}
                                                data-testid={`anpassen-tenant-${t.id}`}
                                                disabled={gesperrt} grund={grund}
                                                hilfe="aktion.createMietanpassung">
                                            <TrendingUp size={12} className="mr-1" /> Miete
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-8" onClick={() => startEdit(t)} data-testid={`edit-tenant-${t.id}`}
                                                disabled={gesperrt} grund={grund}>
                                            <Pencil size={12} className="mr-1" /> Bearbeiten
                                        </Button>
                                        <DeleteConfirm
                                            label={t.full_name}
                                            kind="tenant"
                                            onDelete={(cascade) => deleteTenant(t.id, cascade)}
                                            onDeleted={load}
                                            testid={`delete-tenant-${t.id}`}
                                            disabled={gesperrt}
                                            grund={grund}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                        {tenants.length === 0 && (
                            <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Noch keine Mieter angelegt.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <MieterKontoDialog
                tenantId={kontoFuer?.id}
                tenantName={kontoFuer?.full_name}
                open={!!kontoFuer}
                onOpenChange={(o) => { if (!o) setKontoFuer(null); }} />
            <MietanpassungDialog
                mieter={anpassungFuer}
                open={!!anpassungFuer}
                onOpenChange={(o) => { if (!o) setAnpassungFuer(null); }}
                onDone={load}
                gesperrt={gesperrt}
                grund={grund}
            />
        </div>
    );
};

export default Tenants;
